/* eslint-disable react/no-children-prop */
'use client'
import { DestPagesProps, SanityData, SearchItem } from '@/app/types'
import dynamic from 'next/dynamic'
import { FC, useCallback, useEffect, useRef, useState } from 'react'

import Booking from '@/app/components/Booking'
import CompactBooking from '@/app/components/CompactBooking'
import HotelHeaderNavigation from '@/app/components/HotelHeaderNavigation'
import InitCaller from '@/app/components/InitCaller'
import { DisplayData } from '@/app/components/LocationDataHandler'
import Overlay from '@/app/components/Overlay'
import Top from '@/app/components/Top'
import { AvailableInputTypes, SMALL_HEADER_FORM } from '@/app/constants'
import eventBus from '@/app/eventBus'
import { useGrowthbook } from '@/app/hooks/use-growthbook'
import { PS_EVENTS } from '@/app/ps-events'
import { parseSearchParamsToLower } from '@/app/utils'
import { GrowthBookProvider } from '@growthbook/growthbook-react'
import { APIProvider } from '@vis.gl/react-google-maps'
import classNames from 'classnames'
import HotelsLoadingAnimation from './HotelsLoadingAnimation'

const LocationDataHandler = dynamic(
	() => import('@/app/components/LocationDataHandler'),
	{ ssr: false }
)

export interface Props {
	sanityData?: SanityData
	mainPage?: boolean
	isHotelLandingPage?: boolean
	hotelPage?: boolean
	hotel?: any
	params?: DestPagesProps
	loading?: boolean
}

const Header: FC<Props> = (props) => {
	const {
		sanityData,
		mainPage,
		hotelPage,
		params,
		loading,
		isHotelLandingPage,
	} = props
	const [targetLocation, setTargetLocation] = useState<SearchItem | null>(null)
	const [displayValue, setDisplayValue] = useState<DisplayData | null>(null)
	const { growthbook, gbCallback } = useGrowthbook()
	// Will be used with GrowthBook
	// const [userId, setUserId] = useState('')
	const [isSticky, setIsSticky] = useState(false)
	const searchParams = parseSearchParamsToLower(params?.searchParams)
	const destCode = searchParams?.destcode
	const lang = params?.params.lang
	const isExpandedRef = useRef(false)
	const [forceUpdate, setForceUpdate] = useState(false)
	const [openedInput, setOpenedInput] = useState<AvailableInputTypes>(null)
	const prevSearchParamsRef = useRef<string>('')
	const headerRef = useRef<HTMLDivElement>(null)
	const [isAutoScrolling, setIsAutoScrolling] = useState(false)

	const getTargetLocation = (item: SearchItem): SearchItem => {
		setTargetLocation(item)
		return item
	}

	const getDisplayValue = (item: DisplayData): DisplayData => {
		setDisplayValue(item)
		return item
	}

	const handleToggleExpand = () => {
		isExpandedRef.current = !isExpandedRef.current
		setForceUpdate((prev) => !prev)
		setOpenedInput(null)
	}

	// Keeping for future GB experiments
	// useEffect(() => {
	// 	if (userId && lang === 'en') {
	// 		gbCallback({ uid: userId })
	// 	}
	// }, [userId])

	// `home_page_view` is emitted solely by RenderGA (pageName='home_page') on the
	// (main) layout; emitting it here too was a redundant second source for the
	// same event name (THP-101 / BHP-1632 A.2).

	const handleScroll = useCallback(() => {
		const scrollPosition = window.scrollY
		const hotelPageStickyTreshold = 80

		let stickyTreshold = 120

		if (hotelPage) {
			stickyTreshold = hotelPageStickyTreshold
		}

		// Only update sticky state if it's not an auto-scroll
		if (!isAutoScrolling) {
			setIsSticky(scrollPosition > stickyTreshold)
			if (isExpandedRef.current && scrollPosition > 100) {
				isExpandedRef.current = false
				setForceUpdate((prev) => !prev)
			}
		}
	}, [isAutoScrolling])

	useEffect(() => {
		const handleAutoScroll = () => {
			setIsAutoScrolling(true)
			setTimeout(() => setIsAutoScrolling(false), 1000) // Adjust timeout as needed
		}

		eventBus.subscribe(PS_EVENTS.AUTO_SCROLL, handleAutoScroll)

		const handleClickOutside = (event: MouseEvent) => {
			if (
				headerRef.current &&
				!headerRef.current.contains(event.target as Node) &&
				isExpandedRef.current
			) {
				isExpandedRef.current = false
				setForceUpdate((prev) => !prev)
			}
		}

		if (!mainPage) {
			window.addEventListener('scroll', handleScroll)
		}
		document.addEventListener('mousedown', handleClickOutside)

		return () => {
			window.removeEventListener('scroll', handleScroll)
			document.removeEventListener('mousedown', handleClickOutside)
			eventBus.unsubscribe(PS_EVENTS.AUTO_SCROLL, handleAutoScroll)
		}
	}, [handleScroll, mainPage])

	useEffect(() => {
		if (isExpandedRef.current) {
			setOpenedInput(SMALL_HEADER_FORM.STICKY_HEADER)
		} else {
			setOpenedInput(null)
		}
	}, [isExpandedRef.current])

	const isDefaultLocation =
		!mainPage || destCode || isHotelLandingPage ? targetLocation : null
	const shouldLoadLocationData = !mainPage || destCode || isHotelLandingPage

	const shouldShowHotelNavigation = hotelPage && isSticky

	useEffect(() => {
		// Closing the header after searchParams have actually changed
		const currentSearchParamsString = JSON.stringify(searchParams)

		if (isExpandedRef.current) {
			if (currentSearchParamsString !== prevSearchParamsRef.current) {
				isExpandedRef.current = false
				setForceUpdate((prev) => !prev)
				setOpenedInput(null)
			}
		}

		prevSearchParamsRef.current = currentSearchParamsString
	}, [searchParams])

	return (
		<>
			<GrowthBookProvider growthbook={growthbook}>
				{/* Hotel Header Navigation - only render on hotel pages */}
				{hotelPage && (
					<HotelHeaderNavigation
						isSticky={isSticky}
						isVisible={shouldShowHotelNavigation}
					/>
				)}

				{/* Regular Header */}
				<APIProvider apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || ''}>
					<header
						ref={headerRef}
						className={classNames('header', {
							'header--sticky': isSticky || isExpandedRef.current,
							'header--expanded': isExpandedRef.current,
							'bg-white': !mainPage,
							'header--hidden': shouldShowHotelNavigation,
						})}
						data-test-id="header"
					>
						<Top
							lang={lang as string}
							isMain={mainPage}
							isSticky={isSticky}
							children={{
								middle: !mainPage && (
									<CompactBooking
										displayValue={displayValue || ({} as DisplayData)}
										defaultLocation={isDefaultLocation}
										isMainPage={!!mainPage}
										sanityData={sanityData}
										loading={loading}
										customHeading={!!destCode}
										isHotelPage={hotelPage}
										isExpanded={isExpandedRef.current}
										onToggleExpand={handleToggleExpand}
										dest={params?.params.dest}
									/>
								),
								bottom: !mainPage && (
									<div
										className={classNames(`compact-booking__content`, {
											[`compact-booking__content--expanded`]:
												isExpandedRef.current,
											[`compact-booking__content--collapsed`]:
												!isExpandedRef.current,
										})}
									>
										<Booking
											displayValue={displayValue || ({} as DisplayData)}
											defaultLocation={isDefaultLocation}
											isMainPage={mainPage}
											sanityData={sanityData}
											loading={loading}
											customHeading={!!destCode}
											isHotelPage={hotelPage}
											isExpanded={isExpandedRef.current}
											onClose={() => {
												isExpandedRef.current = false
												setForceUpdate((prev) => !prev)
											}}
										/>
									</div>
								),
							}}
						/>
					</header>
					<div
						className={classNames('header-wrapper', {
							// hidden: !mainPage,
							'max-lg:py-3 max-lg:px-2': mainPage,
						})}
					>
						<InitCaller
							sanityData={sanityData}
							// setUserId={(id: string) => setUserId(id)}
							isHomePage={mainPage as boolean}
						/>
						{shouldLoadLocationData && (
							<LocationDataHandler
								params={props.params?.params as DestPagesProps['params']}
								searchParams={
									props.params?.searchParams as DestPagesProps['searchParams']
								}
								setDisplayValue={getDisplayValue}
								setTargetLocation={getTargetLocation}
								mainPage={mainPage}
							/>
						)}
						{mainPage && (
							<Booking
								hotel={props.hotel}
								displayValue={displayValue || ({} as DisplayData)}
								defaultLocation={isDefaultLocation}
								isMainPage={mainPage}
								sanityData={sanityData}
								loading={loading}
								customHeading={!!destCode}
								isHotelPage={hotelPage}
								isHotelLandingPage={isHotelLandingPage}
							/>
						)}
						{props.loading ? <HotelsLoadingAnimation /> : null}
					</div>
					<Overlay
						isOpened={openedInput !== null}
						openInput={handleToggleExpand}
						onClick={handleToggleExpand}
					/>
				</APIProvider>
			</GrowthBookProvider>
		</>
	)
}

export default Header
